// Stable, non-PII identifiers for the analytics stream.
//
// device_id  -> persists across sessions for a browser. Written to a cookie on
//               the shared registrable domain (retira.app) so a demo visit and a
//               later signed-in studio session on studio.retira.app can be
//               stitched together. Contains no personal data: a random UUID.
// session_id -> per tab load (sessionStorage), so we can group a single visit.
import { getSharedCookieDomain } from '@/lib/hostRouting'

const DEVICE_KEY = 'retira.analytics.device'
const SESSION_KEY = 'retira.analytics.session'
const DEVICE_COOKIE = 'retira_aid'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

function randomId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

function readCookie(name) {
  if (typeof document === 'undefined') return ''
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : ''
}

function writeDeviceCookie(value) {
  if (typeof document === 'undefined') return
  const domain = getSharedCookieDomain()
  const parts = [
    `${DEVICE_COOKIE}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${ONE_YEAR_SECONDS}`,
    'samesite=lax',
  ]
  if (domain) parts.push(`domain=.${domain}`)
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:') parts.push('secure')
  document.cookie = parts.join('; ')
}

let cachedDeviceId = ''

export function getDeviceId() {
  if (cachedDeviceId) return cachedDeviceId
  if (typeof window === 'undefined') return ''

  // Prefer the shared cookie so studio + demo agree on the same id.
  let id = readCookie(DEVICE_COOKIE)
  try {
    if (!id) id = window.localStorage.getItem(DEVICE_KEY) || ''
  } catch {
    // ignore
  }
  if (!id) id = randomId()

  try {
    window.localStorage.setItem(DEVICE_KEY, id)
  } catch {
    // ignore
  }
  writeDeviceCookie(id)
  cachedDeviceId = id
  return id
}

let cachedSessionId = ''

export function getSessionId() {
  if (cachedSessionId) return cachedSessionId
  if (typeof window === 'undefined') return ''
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY) || ''
    if (!id) {
      id = randomId()
      window.sessionStorage.setItem(SESSION_KEY, id)
    }
    cachedSessionId = id
    return id
  } catch {
    cachedSessionId = cachedSessionId || randomId()
    return cachedSessionId
  }
}
